import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Copy, LogOut, Play, Users } from "lucide-react";
import { toast } from "sonner";
import { getStudentId } from "@/lib/api";
import { getStudyState, leaveStudySession, startStudySession } from "@/lib/studyApi";
import { useStudySocket } from "@/hooks/useStudySocket";

export default function StudyLobby() {
  const { code } = useParams();
  const navigate = useNavigate();
  const studentId = getStudentId();
  const [state, setState] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = () => {
    getStudyState(code, studentId).then(({ data }) => setState(data)).catch(() => {
      toast.error("Couldn't load session.");
      navigate("/");
    });
  };

  useEffect(() => { load(); }, [code]);

  useStudySocket(code, studentId, (msg) => {
    if (msg.type === "STUDENT_JOINED" || msg.type === "STUDENT_LEFT" || msg.type === "HOST_TRANSFERRED") load();
    if (msg.type === "LESSON_STARTED") navigate(`/study/live/${code}`);
  });

  const copyId = () => {
    navigator.clipboard.writeText(code);
    toast.success("Session ID copied!");
  };

  const start = async () => {
    setStarting(true);
    try {
      await startStudySession(code, studentId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the session.");
      setStarting(false);
    }
  };

  const leave = async () => {
    await leaveStudySession(code, studentId).catch(() => {});
    navigate("/");
  };

  if (!state) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  const isHost = state.my_role === "host";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="study-lobby-page">
      <div className="max-w-md w-full bg-card border border-border rounded-3xl shadow-lg p-7 text-center">
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">Session ID</p>
        <div className="flex items-center justify-center gap-2 mb-1">
          <p className="font-heading font-extrabold text-3xl tracking-wider" data-testid="lobby-session-code">{code}</p>
          <button onClick={copyId} data-testid="lobby-copy-id-button" className="text-muted-foreground hover:text-foreground">
            <Copy size={18} />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-6">{state.session.subject} · {state.session.topic}</p>

        <div className="flex items-center justify-center gap-1.5 mb-2 text-sm font-bold text-muted-foreground">
          <Users size={15} /> Participants ({state.participants.length}/3)
        </div>
        <div className="space-y-2 mb-6" data-testid="lobby-participants-list">
          {state.participants.map((p) => (
            <div key={p.student_id} className="flex items-center justify-between bg-muted rounded-xl px-4 py-2.5" data-testid={`lobby-participant-${p.student_id}`}>
              <span className="text-sm font-semibold">{p.display_name} {p.role === "host" && <span className="text-primary text-xs">(Host)</span>}</span>
              <span className={`w-2 h-2 rounded-full ${p.connection_status === "connected" ? "bg-success" : "bg-muted-foreground/40"}`} />
            </div>
          ))}
        </div>

        {isHost ? (
          <button onClick={start} disabled={starting} data-testid="lobby-start-session-button"
            className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2 hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-60 mb-2">
            <Play size={16} /> {starting ? "Starting..." : "Start Session"}
          </button>
        ) : (
          <p className="text-sm text-muted-foreground mb-2" data-testid="lobby-waiting-message">Waiting for the host to start the session...</p>
        )}
        <button onClick={leave} data-testid="lobby-leave-button" className="w-full h-11 rounded-xl border border-border text-muted-foreground font-semibold flex items-center justify-center gap-2 hover:bg-muted">
          <LogOut size={15} /> Leave
        </button>
      </div>
    </div>
  );
}
